import { memo, useEffect, useRef, useState } from 'react';
import { HtmlCodeEditor } from '@/shared/ui/HtmlCodeEditor';
import {
  Button,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { SaveIcon } from 'lucide-react';
import { getWidgetStores } from '@/entities/widget';
import { notificationService } from '@/lib/services/notification.service';
import { CustomCardWithInput } from '../CustomCard';
import type { CodeViewProps } from './CodeView.types';
import type { EmailData } from '../../../model/email-flow-widget.types';
import { useTranslation } from 'react-i18next';

const DEFAULT_AGENT_ID = 'email-flow-agent';
const DEFAULT_WIDGET_KEY = 'email-flow-widget:v1';

export const CodeView = memo(
  ({
    emailData,
    onChange,
    agentId: agentIdProp,
    widgetSchemaKey,
    hideSaveButton,
  }: CodeViewProps) => {
    const { t } = useTranslation('contentTest');

    const agentId = agentIdProp ?? DEFAULT_AGENT_ID;
    const widgetKey = widgetSchemaKey ?? DEFAULT_WIDGET_KEY;
    const lastNotifiedHtmlContent = useRef<string>(emailData.htmlContent);
    const lastNotifiedPreheaderText = useRef<string>(emailData.preheaderText);
    const lastNotifiedSubjectLine = useRef<string>(emailData.subjectLine);
    const [editorValue, setEditorValue] = useState<EmailData>(emailData);
    const isFirstRender = useRef(true);

    // Store access via the widget binding seam.
    // See `entities/widget/lib/widget-store-bindings`.
    const { widgetStore, chatStore, uiStore } = getWidgetStores();
    // Subscribe to ONLY the `getActiveVersion` action via selector — the
    // bare `useWidgetStore()` form subscribes to the entire store, so any
    // unrelated widget action (open/close, version switch) re-rendered
    // CodeMirror. Selector keeps subscription narrow to a stable action ref.
    //
    // `getActiveVersion` is
    // declared on `WidgetWidgetStoreState`, so the selector resolves to
    // the typed action without an inline `as unknown as { ... }` cast.
    // Contract enforcement lives at the wiring site (`main.tsx` →
    // `configureWidgetStores`) — runtime renames are caught at compile
    // time there, not via silent widening here.
    const getActiveVersion = widgetStore.useWidgetStore((s) => s.getActiveVersion);
    const setWidgetUnsavedEdits = widgetStore.useWidgetStore((s) => s.setWidgetUnsavedEdits);
    const chatId = chatStore.useChatId();
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    // Own source key: this tracker leads `useArtifactSave`'s by the 2s notify debounce below, and a
    // shared key would let whichever effect runs last clear the other's flag.
    const dirtySource = `${agentId}:code`;
    // Same reason as the selector above: read `storeWidgetVersion` and
    // `getActiveVersion` off the typed snapshot rather than casting.
    const widgetPanel = widgetStore.useWidgetStore.getState();

    const { mutateAsync: createArtifactVersion, isPending } =
      widgetStore.useCreateArtifactVersion() as {
        mutateAsync: (params: {
          chatId: string;
          exchangeId: string;
          key: string;
          name: string;
          kind: string;
          data: Record<string, unknown>;
        }) => Promise<{
          key: string;
          name: string;
          data: Record<string, unknown>;
          version: number;
          exchangeId: string;
        }>;
        isPending: boolean;
      };
    const { sidePanelLayoutMode } = uiStore.useSidePanelLayoutMode();
    const isFullscreen = sidePanelLayoutMode === 'fullscreen';

    // Keep local editor value in sync when the upstream content changes
    useEffect(() => {
      // Only update if the content change is NOT from our own onChange notification
      if (
        emailData.htmlContent !== lastNotifiedHtmlContent.current ||
        emailData.preheaderText !== lastNotifiedPreheaderText.current ||
        emailData.subjectLine != lastNotifiedSubjectLine.current
      ) {
        setEditorValue(emailData);
        // Advance the baseline in the SAME step. This branch means the change came from upstream — an
        // agent revision, a version switch, a save elsewhere — so it is already saved and is not a
        // local edit. Without this the dirty effect below compared the new value against the old refs
        // and reported dirty until the 2s debounce caught up, firing the composer's send warning on
        // every artifact delivery even while the user was on the Preview tab.
        lastNotifiedHtmlContent.current = emailData.htmlContent;
        lastNotifiedPreheaderText.current = emailData.preheaderText;
        lastNotifiedSubjectLine.current = emailData.subjectLine;
      }
    }, [emailData]);

    // Sync saved subject line and preheader text when props change. Also published to the widget store
    // so the chat composer can warn on send: during the 2s debounce below the parent has not seen the
    // edit yet, so `useArtifactSave` still looks clean and would miss it.
    //
    // This effect can only ever SET the store flag, never clear it: it compares against refs, which
    // are not reactive deps, so it does not re-run when the debounce updates them. Clearing therefore
    // happens in the debounce itself (below) — the moment the edit stops being CodeView-local.
    useEffect(() => {
      const dirty =
        editorValue.htmlContent !== lastNotifiedHtmlContent.current ||
        editorValue.preheaderText !== lastNotifiedPreheaderText.current ||
        editorValue.subjectLine != lastNotifiedSubjectLine.current;
      setHasUnsavedChanges(dirty);
      if (dirty) setWidgetUnsavedEdits(dirtySource, true);
    }, [editorValue, dirtySource, setWidgetUnsavedEdits]);

    useEffect(
      () => () => setWidgetUnsavedEdits(dirtySource, false),
      [dirtySource, setWidgetUnsavedEdits],
    );

    // Debounce notifying the parent by 2 seconds
    useEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }
      const timer = setTimeout(() => {
        onChange?.(editorValue);
        lastNotifiedHtmlContent.current = editorValue.htmlContent;
        lastNotifiedPreheaderText.current = editorValue.preheaderText;
        lastNotifiedSubjectLine.current = editorValue.subjectLine;
        // The parent now holds this edit, so `useArtifactSave`'s `:preview` key covers it and this
        // one must stand down. Cleared here rather than in the dirty effect above, which compares
        // against these refs and so never re-runs once they catch up — without this the flag latched
        // true for the session and the composer's unsaved-edits prompt fired on every send.
        setWidgetUnsavedEdits(dirtySource, false);
      }, 2000);
      return () => clearTimeout(timer);
    }, [editorValue, onChange, dirtySource, setWidgetUnsavedEdits]);

    const onSaveHtml = async () => {
      const activeVersion = getActiveVersion(agentId);

      if (!chatId || !activeVersion || !activeVersion.widget) {
        notificationService.showError(t('emailFlowWidget.codeView.failedToSaveTemplate'), {
          persist: false,
          autoHideDuration: 3000,
        });

        return;
      }

      try {
        setHasUnsavedChanges(false);
        setWidgetUnsavedEdits(dirtySource, false);
        const res = await createArtifactVersion({
          chatId: chatId,
          exchangeId: activeVersion.widget.exchangeId,
          key: widgetKey,
          // eslint-disable-next-line i18next/no-literal-string -- persisted artifact name that `apps/api` writes with the same literal; getArtifactDisplayName translates it on read
          name: 'Email Flow Widget',
          // eslint-disable-next-line i18next/no-literal-string -- artifact kind wire enum value
          kind: 'email',
          data: {
            ...(activeVersion.widget.data as object),
            htmlContent: editorValue.htmlContent,
            preheaderText: editorValue.preheaderText,
            subjectLine: editorValue.subjectLine,
          },
        });

        widgetPanel.storeWidgetVersion(
          widgetPanel.getActiveVersion(agentId)?.messageId ?? '',
          agentId,
          {
            key: res.key,
            agentName: res.name,
            data: res.data,
            version: res.version,
            exchangeId: res.exchangeId,
          },
          res.data.subjectLine as string,
        );

        notificationService.showSuccess(t('emailFlowWidget.codeView.templateSavedSuccessfully'));
      } catch {
        setHasUnsavedChanges(true);
        setWidgetUnsavedEdits(dirtySource, true);
        notificationService.showError(t('emailFlowWidget.codeView.failedToSaveTemplate'), {
          persist: false,
          autoHideDuration: 3000,
        });
      }
    };

    const cards = (
      <>
        <CustomCardWithInput
          title={t('emailFlowWidget.codeView.subjectLine')}
          // eslint-disable-next-line i18next/no-literal-string -- email-platform merge tag, substituted by the sending platform, must stay literal
          placeholder="{SubjectLine}"
          initialInputValue={editorValue.subjectLine}
          onChange={(e) => setEditorValue({ ...editorValue, subjectLine: e.target.value })}
        />
        <CustomCardWithInput
          title={t('emailFlowWidget.codeView.preHeaderText')}
          // eslint-disable-next-line i18next/no-literal-string -- email-platform merge tag, substituted by the sending platform, must stay literal
          placeholder="{PreHeaderText}"
          initialInputValue={editorValue.preheaderText}
          onChange={(e) => setEditorValue({ ...editorValue, preheaderText: e.target.value })}
        />
      </>
    );
    const subjectAndPreheader = isFullscreen ? (
      <div className="flex flex-col gap-4 pl-10 pb-4">{cards}</div>
    ) : (
      <div className="flex flex-col lg:flex-row gap-4 px-10 pb-4 w-full">{cards}</div>
    );
    const codeContent = (
      <div
        className={`mr-10 mb-6 rounded-3xl border border-border p-6 ${
          isFullscreen ? 'ml-6' : 'ml-10'
        }`}
      >
        <HtmlCodeEditor
          value={editorValue.htmlContent}
          onChange={(value) => setEditorValue({ ...editorValue, htmlContent: value })}
          className={isFullscreen ? 'pr-6' : ''}
        />
      </div>
    );

    return (
      <>
        <div className={isFullscreen ? 'flex gap-4 w-full' : ''}>
          <div className={isFullscreen ? 'w-125 shrink-0' : ''}>{subjectAndPreheader}</div>
          <div className={isFullscreen ? 'flex-1' : ''}>{codeContent}</div>
        </div>
        {!hideSaveButton && isPending && (
          <div className="pointer-events-none absolute inset-0 z-1 bg-background/50 backdrop-blur-[2px]" />
        )}
        {!hideSaveButton && (
          <div className="sticky bottom-0 h-[9%] w-full bg-card">
            <div className="float-right mt-2.75 mr-1.25">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={t('emailFlowWidget.codeView.save2')}
                      onClick={() => void onSaveHtml()}
                      disabled={!hasUnsavedChanges || isPending}
                    >
                      {isPending ? <Spinner aria-hidden size="lg" /> : <SaveIcon />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {t('emailFlowWidget.codeView.save')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        )}
      </>
    );
  },
);

CodeView.displayName = 'EmailFlowCodeView';
